import {RefObject, useCallback} from 'react';
import {useFocusEffect} from '@react-navigation/native';
import {
  findPromotionAnchorIndex,
  PromotionAnchorItem,
  PromotionAnchorParams,
} from './promotion-anchor.utils';

interface UsePromotionAnchorOptions {
  ready: boolean;
  anchorItems: PromotionAnchorItem[];
  anchorParams: PromotionAnchorParams;
  cardRefs: RefObject<Array<HTMLDivElement | null>>;
}

const FOCUS_TRANSITION_DELAY_MS = 350;

export function usePromotionAnchor({
  ready,
  anchorItems,
  anchorParams,
  cardRefs,
}: UsePromotionAnchorOptions) {
  const {tabKey, entryId, ptype, id} = anchorParams;

  useFocusEffect(
    useCallback(() => {
      if (!ready) {
        return;
      }

      const anchorIndex = findPromotionAnchorIndex(anchorItems, {
        tabKey,
        entryId,
        ptype,
        id,
      });
      if (anchorIndex < 0) {
        return;
      }

      let frameId: number;
      const scrollToAnchor = () => {
        cardRefs.current?.[anchorIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      };
      const requestScroll = () => {
        frameId = requestAnimationFrame(scrollToAnchor);
      };

      requestScroll();
      const transitionTimer = window.setTimeout(
        requestScroll,
        FOCUS_TRANSITION_DELAY_MS,
      );

      return () => {
        cancelAnimationFrame(frameId);
        window.clearTimeout(transitionTimer);
      };
    }, [anchorItems, cardRefs, entryId, id, ptype, ready, tabKey]),
  );
}
